import { Form, Select, Empty } from 'antd';
import { Alert } from 'antd';
import {
  readStructuredRawForLang, writeStructuredRawForLang, readBlobKind,
  EMPTY_STRUCTURED, EMPTY_SCHEDULE, EMPTY_MESSAGE,
  type TopicDetails, type StructuredKind,
  type StructuredData, type ScheduleStructured, type MessageStructured,
} from '@/types/topic';
import { KIND_BY_TYPE_ID, resolveKind, KIND_LABELS } from './structured/kinds';
import { MortgageForm } from './structured/MortgageForm';
import { ScheduleForm } from './structured/ScheduleForm';
import { MessageForm } from './structured/MessageForm';

interface Props {
  typeId: number;
  details: TopicDetails;
  /** Active display language (from the page language switcher). The source
   *  language edits the canonical blob; others edit structured_i18n[lang]. */
  lang: string;
  onChange: (next: TopicDetails) => void;
}

const EMPTY_BY_KIND: Record<StructuredKind, Record<string, unknown>> = {
  mortgage: { ...EMPTY_STRUCTURED },
  schedule: { ...EMPTY_SCHEDULE },
  message: { ...EMPTY_MESSAGE },
  none: {},
};

export function StructuredDataTab({ typeId, details, lang, onChange }: Props) {
  const isSource = lang === 'en';
  const raw = readStructuredRawForLang(details, lang);
  // Kind is language-agnostic — always read from the canonical blob.
  const blobKind = readBlobKind(details);
  const isMix = KIND_BY_TYPE_ID[typeId] === 'select';
  const activeKind = resolveKind(typeId, blobKind);

  const setRaw = (obj: Record<string, unknown>, kind: StructuredKind) =>
    onChange(writeStructuredRawForLang(details, lang, { ...obj, kind }));

  const changeKind = (kind: StructuredKind) =>
    onChange(writeStructuredRawForLang(details, lang, { ...EMPTY_BY_KIND[kind], kind }));

  return (
    <>
      {!isSource && (
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message={`Editing the ${lang} translation of the structured data. Numbers, %, $, states and dates should stay identical to the source; only translate the text.`}
        />
      )}
      {isMix && (
        <Form layout="vertical" style={{ marginBottom: 8 }}>
          <Form.Item label="Structured format" style={{ maxWidth: 280, marginBottom: 8 }}>
            <Select<StructuredKind>
              value={activeKind}
              onChange={changeKind}
              options={(['none', 'mortgage', 'schedule', 'message'] as StructuredKind[]).map((k) => ({
                value: k,
                label: KIND_LABELS[k],
              }))}
            />
          </Form.Item>
        </Form>
      )}

      {activeKind === 'mortgage' && (
        <MortgageForm
          value={{ ...EMPTY_STRUCTURED, ...raw } as StructuredData}
          onChange={(s) => setRaw(s as unknown as Record<string, unknown>, 'mortgage')}
        />
      )}
      {activeKind === 'schedule' && (
        <ScheduleForm
          value={{ ...EMPTY_SCHEDULE, ...raw } as ScheduleStructured}
          onChange={(s) => setRaw(s as unknown as Record<string, unknown>, 'schedule')}
        />
      )}
      {activeKind === 'message' && (
        <MessageForm
          value={{ ...EMPTY_MESSAGE, ...raw } as MessageStructured}
          onChange={(s) => setRaw(s as unknown as Record<string, unknown>, 'message')}
        />
      )}
      {activeKind === 'none' && <Empty description="No structured format selected" />}
    </>
  );
}
